/**
 * Sponsor invite email HTML assembly (admin preview — keep in sync with Edge `sponsor-invite-email.ts`).
 */

export interface SponsorInviteTemplateRow {
  step: number;
  subject: string;
  body: string;
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function escapeHtmlAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

/** Turn plain text body into simple HTML paragraphs. */
export function bodyTextToHtml(body: string): string {
  const trimmed = body.trim();
  if (!trimmed) return '<p></p>';
  if (/<[a-z][\s\S]*>/i.test(trimmed)) {
    return trimmed;
  }
  return trimmed
    .split(/\n\n+/)
    .map((block) => `<p style="margin:0 0 12px 0;">${escapeHtml(block).replace(/\n/g, '<br />')}</p>`)
    .join('');
}

export function buildSponsorInviteCtaHref(baseUrl: string, inviteToken: string): string {
  const base = baseUrl.replace(/\/$/, '');
  return `${base}/sponsor-invite?token=${encodeURIComponent(inviteToken)}`;
}

export const SPONSOR_LINK_PLACEHOLDER = /\{\{sponsor_link\}\}/g;
const SPONSOR_LINK_HTML_SENTINEL = '__SPONSOR_INVITE_LINK__';

/** Replace `{{sponsor_link}}` with the invitation URL (plain-text / pre-HTML). */
export function substituteSponsorInviteLink(body: string, ctaHref: string): string {
  return body.replace(SPONSOR_LINK_PLACEHOLDER, ctaHref);
}

/** Labelled invite link — fixed visible text per deliverability report ("never raw URLs"). */
export function buildSponsorInviteLinkHtml(ctaHref: string): string {
  const href = escapeHtmlAttr(ctaHref);
  const label = 'View your invitation';
  return `<a href="${href}" style="color:#0066cc; text-decoration:underline;">${label}</a>`;
}

const HTML_CTA_PREFIX = 'View the invitation here:';
const PLAIN_CTA_PREFIX = 'View the invitation here:';

export function bodyContainsSponsorLinkPlaceholder(body: string): boolean {
  return body.includes('{{sponsor_link}}');
}

/** True when invite URL is already present after placeholder substitution. */
export function bodyAlreadyContainsInviteLink(body: string, ctaHref: string): boolean {
  return body.includes(ctaHref);
}

export function buildExplicitHtmlCtaBlock(ctaHref: string): string {
  const linkHtml = buildSponsorInviteLinkHtml(ctaHref);
  return `<p>${HTML_CTA_PREFIX} ${linkHtml}</p>`;
}

/** Inner HTML: body with `{{sponsor_link}}` as anchor; fallback CTA if placeholder absent. */
export function buildSponsorInviteEmailInnerHtml(params: {
  body: string;
  ctaHref: string;
}): string {
  const hadPlaceholder = bodyContainsSponsorLinkPlaceholder(params.body);
  const withSentinel = params.body.replace(SPONSOR_LINK_PLACEHOLDER, SPONSOR_LINK_HTML_SENTINEL);
  const bodyHtml = bodyTextToHtml(withSentinel);
  const linkHtml = buildSponsorInviteLinkHtml(params.ctaHref);
  const inner = bodyHtml.split(SPONSOR_LINK_HTML_SENTINEL).join(linkHtml);

  const resolvedBody = substituteSponsorInviteLink(params.body, params.ctaHref);
  const needsFallback =
    !hadPlaceholder && !bodyAlreadyContainsInviteLink(resolvedBody, params.ctaHref);

  if (!needsFallback) {
    return inner;
  }

  return `${inner}\n${buildExplicitHtmlCtaBlock(params.ctaHref)}`;
}

const LEGAL_COMPANY_NAME = 'Pharma Connect Navigator';
const PHYSICAL_ADDRESS = 'Cambridge, MA 02114, USA';
const SUPPORT_EMAIL = 'support@pharmaconnectnavigator.com';

const SPONSOR_INVITE_UNSUBSCRIBE_REASON =
  "You&rsquo;re receiving this invitation because of your work in biotech/pharma development and external manufacturing partner selection. If this isn&rsquo;t relevant, you can unsubscribe below.";

/** Slim sponsor-invite footer (mirrors Edge `getEmailFooter(..., 'sponsor_invite')`). */
export function buildSponsorInviteFooterHtml(unsubscribeUrl = '#'): string {
  const unsubscribeLine = `<a href="${escapeHtmlAttr(unsubscribeUrl)}" style="color: #0066cc; text-decoration: underline;">Unsubscribe</a>`;
  return `
    <hr style="border: 0; border-top: 1px solid #eee; margin: 20px 0 20px; background: #ffffff;" />
    <div style="font-size: 11px; color: #666; line-height: 1.5; margin-top: 24px;">
      ${escapeHtml(LEGAL_COMPANY_NAME)}<br />
      ${escapeHtml(PHYSICAL_ADDRESS)}<br />
      ${SPONSOR_INVITE_UNSUBSCRIBE_REASON}<br />
      ${unsubscribeLine} | <a href="mailto:${SUPPORT_EMAIL}" style="color: #0066cc; text-decoration: underline;">${SUPPORT_EMAIL}</a>
    </div>
  `;
}

/** Document wrapper (Phase 1.1 — plain left-aligned body, no card/table). */
export function wrapSponsorInviteEmailDocument(innerHtml: string, footerHtml: string): string {
  return `
    <!DOCTYPE html>
    <html lang="en">
    <body style="margin:0; font-family:Arial,sans-serif; color:#333; background:#fff; line-height:1.5;">
      ${innerHtml}
      ${footerHtml}
    </body>
    </html>
  `;
}

/** Plain-text MIME part (mirrors Edge `buildSponsorInvitePlainText`). */
export function buildSponsorInvitePlainText(params: {
  body: string;
  ctaHref: string;
  unsubscribeUrl: string;
}): string {
  const trimmedBody = substituteSponsorInviteLink(params.body, params.ctaHref).trim();
  const explicitCtaLine = `${PLAIN_CTA_PREFIX} ${params.ctaHref}`;
  const ctaBlock = bodyAlreadyContainsInviteLink(trimmedBody, params.ctaHref)
    ? []
    : ['', explicitCtaLine];
  const unsubscribeReason =
    "You're receiving this invitation because of your work in biotech/pharma development and external manufacturing partner selection. If this isn't relevant, you can unsubscribe below.";
  return [
    trimmedBody,
    ...ctaBlock,
    '',
    '—',
    LEGAL_COMPANY_NAME,
    PHYSICAL_ADDRESS,
    unsubscribeReason,
    `Unsubscribe: ${params.unsubscribeUrl}`,
    `Questions: ${SUPPORT_EMAIL}`,
  ].join('\n');
}

/**
 * Full HTML preview document — same assembly order as Edge sends (inner → footer → wrap).
 * `body` should already have {{greeting}} resolved for preview (see SponsorInviteCampaignPanel).
 * Include `{{sponsor_link}}` in the template body where the invitation URL should appear.
 */
export function buildSponsorInvitePreviewHtml(params: {
  body: string;
  ctaHref: string;
  unsubscribeUrl?: string;
}): string {
  const inner = buildSponsorInviteEmailInnerHtml({
    body: params.body,
    ctaHref: params.ctaHref,
  });
  const footer = buildSponsorInviteFooterHtml(params.unsubscribeUrl);
  return wrapSponsorInviteEmailDocument(inner, footer);
}

/** @deprecated Use buildSponsorInvitePreviewHtml — kept for tests and gradual migration. */
export function buildSponsorInviteEmailHtml(params: {
  template: Pick<SponsorInviteTemplateRow, 'body'>;
  ctaHref: string;
  unsubscribeUrl?: string;
  footerHtml?: string;
}): string {
  if (params.footerHtml) {
    const inner = buildSponsorInviteEmailInnerHtml({
      body: params.template.body,
      ctaHref: params.ctaHref,
    });
    return wrapSponsorInviteEmailDocument(inner, params.footerHtml);
  }
  return buildSponsorInvitePreviewHtml({
    body: params.template.body,
    ctaHref: params.ctaHref,
    unsubscribeUrl: params.unsubscribeUrl,
  });
}
